// src/Auth/employees.js
import { auth, db } from "./config";
import {
  doc,
  deleteDoc,
  collection,
  getDocs,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { sendEmployeeInviteEmail } from "./emailService";

const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_BASE;

// Generate a temporary password

// ✅ Add employee using Cloud Function
export async function addEmployee(companyId, employeeData) {
  const {
    name,
    lastName,
    middleName,
    hireDate,
    email,
    role,
    jobTitle,
    group,
    helpdeskRole,
    payRate,
    profileCompleted,
    selfOnboarding,
    timeBankEnabled = false,
    timeBank = {},

    // NEW
    employmentType, // "full_time" | "part_time"
    startDate, // ISO
    endDate, // ISO | null
    ongoing, // boolean
  } = employeeData;

  try {
    if (!auth.currentUser) {
      console.error("❌ No authenticated user found.");
      throw new Error("User is not authenticated.");
    }

    // 🔐 ID Token
    const token = await auth.currentUser.getIdToken(true);

    // Create temp password

    // (Optional) fetch company name for the email subject/body
    let companyName = "";
    try {
      const cSnap = await getDoc(doc(db, "companies", companyId));
      companyName = cSnap.exists() ? cSnap.data().name || "" : "";
    } catch {
      // ignore
    }

    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    const payload = {
      data: {
        name,
        lastName,
        middleName,
        hireDate,
        email: normalizedEmail, // ✅

        companyId,
        role,
        jobTitle,
        group,
        helpdeskRole,
        profileCompleted,
        selfOnboarding,
        timeBankEnabled,
        timeBank,
        payRate,

        // NEW
        employmentType,
        startDate,
        endDate,
        ongoing,
      },
    };

    console.log("📦 Payload (manual):", payload);

    // 🌐 Call HTTPS endpoint with Authorization header
    const res = await fetch(`${FUNCTIONS_BASE}/createEmployee`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const response = await res.json();
    console.log("✅ Manual function response:", response);

    if (!res.ok) {
      throw new Error(response?.error || "Failed to create employee");
    }

    const inviteLink = response?.inviteLink;
    if (!inviteLink) {
      throw new Error("Invite link was not returned from server.");
    }

    await sendEmployeeInviteEmail({
      toEmail: normalizedEmail,
      toName: name,
      companyName,
      inviteLink,
    });

    await updateDoc(doc(db, "companies", companyId), {
      "quickSetupProgress.addedEmployee": true,
    });
  } catch (err) {
    console.error("🔥 Manual error adding employee:", err.message);
    throw new Error("Manual test failed.");
  }
}

// 🔍 Fetch employees
export async function getEmployees(companyId) {
  try {
    const querySnapshot = await getDocs(
      collection(db, `companies/${companyId}/employees`),
    );

    return querySnapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
  } catch (err) {
    console.error("Error fetching employees:", err.message);
    return [];
  }
}

// ❌ Remove employee (server deletes Auth + Firestore)
export async function removeEmployee(companyId, employeeUid) {
  const token = await auth.currentUser.getIdToken(true);

  // Hit the secured function to remove from Auth + Firestore on the server
  const res = await fetch(`${FUNCTIONS_BASE}/deleteEmployee`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ companyId, employeeUid }),
  });

  const payload = await res.json();

  if (!res.ok) {
    throw new Error(payload?.error || "Failed to delete employee");
  }

  // Optional: in case the doc still exists locally, ensure UI sync
  try {
    await deleteDoc(doc(db, `companies/${companyId}/employees`, employeeUid));
  } catch (_) {
    // ignore
  }
}

export async function getEmployeeById(companyId, employeeId) {
  try {
    const employeeRef = doc(db, `companies/${companyId}/employees`, employeeId);
    const docSnap = await getDoc(employeeRef);

    if (!docSnap.exists()) {
      throw new Error("Employee not found.");
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (err) {
    console.error("Error fetching employee details:", err.message);
    throw err;
  }
}

// ✏️ Update employee by ID
export async function updateEmployeeById(companyId, employeeId, updatedData) {
  const employeeRef = doc(db, `companies/${companyId}/employees`, employeeId);
  await updateDoc(employeeRef, updatedData);
}
